import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { subCategoryRepo } from "@repositories";
import { sameOriginImageURL } from "@shared/helpers/constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { useAlert } from "../../contexts/AlertProvider";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { getErrorMessage } from "../../pages/auth/Auth";
import {
  organizationBackgroundImagesQueryKey,
  setBackgroundImageAssignments,
  type BackgroundImageItem,
} from "./imageGalleryApi";
import { useLazyBackgroundImages } from "./useLazyBackgroundImages";

const byName = (a: { name?: string | null }, b: { name?: string | null }) =>
  (a?.name ?? "").localeCompare(b?.name ?? "", "he");

const text = (value?: string | null) => value ?? "";

const UNASSIGNED = "__unassigned__";
const ALL = "";
const ALL_SUBS = "__all__";
const PANEL_MAX_HEIGHT = "calc(100vh - 16rem)";

type SubCategoryOption = {
  id: string;
  name: string;
  categoryId: string;
  categoryName: string;
};

type CategoryOption = {
  id: string;
  name: string;
  subs: SubCategoryOption[];
};

type Props = {
  organizationId: string | undefined;
  images: BackgroundImageItem[];
  onDelete: (imageId: string) => void;
};

export const BackgroundImageManager: FC<Props> = ({
  organizationId,
  images,
  onDelete,
}) => {
  const { currOrganization } = useOrganization();
  const { setAlertText, setSeverity } = useAlert();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState(ALL);
  const [subCategoryId, setSubCategoryId] = useState(ALL_SUBS);
  const [populationId, setPopulationId] = useState<string | null>(null);
  const [populationAnchor, setPopulationAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [unusedOnly, setUnusedOnly] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [draftSubs, setDraftSubs] = useState<string[]>([]);
  const [draftPopulations, setDraftPopulations] = useState<string[]>([]);
  const [openCategoryIds, setOpenCategoryIds] = useState<string[]>([]);
  const [previewFailed, setPreviewFailed] = useState(false);
  const loadedImageId = useRef<string | null>(null);

  const { data: subCategories = [] } = useQuery({
    queryKey: ["organization-sub-categories", organizationId],
    queryFn: async () => {
      const rows = await subCategoryRepo.find({ include: { category: true } });
      return rows.map(
        (row): SubCategoryOption => ({
          id: row.id,
          name: text(row.name),
          categoryId: row.categoryId,
          categoryName: text(row.category?.name),
        }),
      );
    },
    enabled: Boolean(organizationId),
  });

  const populations = useMemo(
    () => (currOrganization?.populations ?? []).slice().sort(byName),
    [currOrganization?.populations],
  );

  const categories = useMemo(() => {
    const byCategory = new Map<string, CategoryOption>();
    for (const sub of subCategories) {
      if (!sub.categoryId) continue;
      const category = byCategory.get(sub.categoryId) ?? {
        id: sub.categoryId,
        name: sub.categoryName,
        subs: [],
      };
      category.subs.push(sub);
      byCategory.set(sub.categoryId, category);
    }
    return [...byCategory.values()]
      .map((category) => ({
        ...category,
        subs: category.subs.slice().sort(byName),
      }))
      .sort(byName);
  }, [subCategories]);

  const known = useMemo(
    () => new Map(subCategories.map((sub) => [sub.id, sub])),
    [subCategories],
  );
  const activeCategory = categories.find(
    (category) => category.id === categoryId,
  );
  const populationName =
    populations.find((population) => population.id === populationId)?.name ??
    "הכל";
  const query = search.trim().toLocaleLowerCase("he");

  const visibleImages = useMemo(() => {
    return images.filter((image) => {
      if (unusedOnly && !image.isUnused) return false;
      if (populationId && !(image.populationIds ?? []).includes(populationId)) {
        return false;
      }
      const linked = (image.subCategoryIds ?? [])
        .map((id) => known.get(id))
        .filter((sub): sub is SubCategoryOption => Boolean(sub));
      if (categoryId === UNASSIGNED && linked.length > 0) return false;
      if (categoryId && categoryId !== UNASSIGNED) {
        const inCategory =
          subCategoryId === ALL_SUBS
            ? linked.some((sub) => sub.categoryId === categoryId)
            : (image.subCategoryIds ?? []).includes(subCategoryId);
        if (!inCategory) return false;
      }
      if (!query) return true;
      const haystack = [
        ...linked.flatMap((sub) => [sub.name, sub.categoryName]),
        ...(linked.length === 0 ? ["ללא שיוך"] : []),
      ]
        .join(" ")
        .toLocaleLowerCase("he");
      return haystack.includes(query);
    });
  }, [
    categoryId,
    images,
    known,
    populationId,
    query,
    subCategoryId,
    unusedOnly,
  ]);

  const selectedImage =
    images.find((image) => image.imageId === selectedImageId) ?? null;

  useEffect(() => {
    if (
      selectedImageId &&
      !images.some((image) => image.imageId === selectedImageId)
    ) {
      setSelectedImageId(null);
    }
  }, [images, selectedImageId]);

  useEffect(() => {
    if (!selectedImage) return;
    if (loadedImageId.current === selectedImage.imageId) return;
    setDraftSubs(selectedImage.subCategoryIds ?? []);
    setDraftPopulations(selectedImage.populationIds ?? []);
    if (categories.length === 0) return;
    loadedImageId.current = selectedImage.imageId;
    setPreviewFailed(false);
    const subs = selectedImage.subCategoryIds ?? [];
    setDraftSubs(subs);
    setDraftPopulations(selectedImage.populationIds ?? []);
    setOpenCategoryIds(categories.map((category) => category.id));
  }, [categories, selectedImage]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedImage) return;
      await setBackgroundImageAssignments({
        imageId: selectedImage.imageId,
        subCategoryIds: draftSubs,
        populationIds: draftPopulations,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: organizationBackgroundImagesQueryKey(organizationId),
      });
      setAlertText("השיוך נשמר");
      setSeverity("success");
    },
    onError: (error) => {
      setAlertText(getErrorMessage(error));
      setSeverity("error");
    },
  });

  const toggleId = (
    current: string[],
    id: string,
    setCurrent: (next: string[]) => void,
  ) => {
    setCurrent(
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );
  };

  const visibleImageIds = visibleImages.map((image) => image.imageId);
  const {
    setFrameNode,
    startedIds,
    loadedImagesStatus,
    markLoaded,
    markError,
  } = useLazyBackgroundImages(visibleImageIds, selectedImageId ?? undefined);

  const captionFor = (image: BackgroundImageItem) => {
    const labels = (image.subCategoryIds ?? [])
      .map((id) => known.get(id)?.name)
      .filter((name): name is string => Boolean(name));
    const assignment = labels.length > 0 ? labels.join(", ") : "ללא שיוך";
    const usage =
      image.isOrganizationDefault && image.opportunityCount === 0
        ? "ברירת מחדל"
        : image.opportunityCount > 0
          ? `ב-${image.opportunityCount} משימות`
          : "לא בשימוש";
    return `${assignment} · ${usage}`;
  };

  return (
    <Stack
      direction="row"
      gap={2}
      alignItems="flex-start"
      sx={{ width: "100%" }}
    >
      <Stack gap={1.5} sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
        >
          <TextField
            size="small"
            value={search}
            placeholder="חיפוש"
            onChange={(event) => setSearch(event.target.value)}
            sx={{ width: 160 }}
          />
          <Select
            size="small"
            value={categoryId}
            onChange={(event) => {
              setCategoryId(event.target.value);
              setSubCategoryId(ALL_SUBS);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value={ALL}>כל הקטגוריות</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name || "קטגוריה"}
              </MenuItem>
            ))}
            <MenuItem value={UNASSIGNED}>ללא שיוך</MenuItem>
          </Select>
          {activeCategory && (
            <Select
              size="small"
              value={subCategoryId}
              onChange={(event) => setSubCategoryId(event.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={ALL_SUBS}>כל התת-קטגוריות</MenuItem>
              {activeCategory.subs.map((sub) => (
                <MenuItem key={sub.id} value={sub.id}>
                  {sub.name || "תת-קטגוריה"}
                </MenuItem>
              ))}
            </Select>
          )}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={unusedOnly}
                onChange={(event) => setUnusedOnly(event.target.checked)}
              />
            }
            label="לא בשימוש"
          />
          {populations.length > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={(event) => setPopulationAnchor(event.currentTarget)}
            >
              {`אוכלוסייה: ${populationName}`}
            </Button>
          )}
          <Menu
            anchorEl={populationAnchor}
            open={Boolean(populationAnchor)}
            onClose={() => setPopulationAnchor(null)}
          >
            <MenuItem
              selected={!populationId}
              onClick={() => {
                setPopulationId(null);
                setPopulationAnchor(null);
              }}
            >
              הכל
            </MenuItem>
            {populations.map((population) => (
              <MenuItem
                key={population.id}
                selected={populationId === population.id}
                onClick={() => {
                  setPopulationId(population.id);
                  setPopulationAnchor(null);
                }}
              >
                {text(population.name)}
              </MenuItem>
            ))}
          </Menu>
        </Stack>

        <Box sx={{ maxHeight: PANEL_MAX_HEIGHT, overflow: "auto" }}>
          {visibleImages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              אין תמונות להצגה.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                gap: 1.5,
              }}
            >
              {visibleImages.map((image) => {
                const selected = image.imageId === selectedImageId;
                const status = loadedImagesStatus[image.imageId];
                const started = startedIds.has(image.imageId);
                return (
                  <Box
                    key={image.imageId}
                    onClick={() => {
                      if (image.imageId !== selectedImageId) {
                        loadedImageId.current = null;
                      }
                      setSelectedImageId(image.imageId);
                    }}
                    sx={{ cursor: "pointer", minWidth: 0 }}
                  >
                    <Box
                      ref={(node: HTMLDivElement | null) =>
                        setFrameNode(image.imageId, node)
                      }
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: 110,
                        borderRadius: 1,
                        overflow: "hidden",
                        border: "3px solid",
                        borderColor: selected ? "primary.main" : "transparent",
                      }}
                    >
                      {started && (
                        <Box
                          component="img"
                          src={sameOriginImageURL(image.imageId)}
                          alt="תמונת רקע"
                          onLoad={() => markLoaded(image.imageId)}
                          onError={() => markError(image.imageId)}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                            opacity: status === "loaded" ? 1 : 0,
                          }}
                        />
                      )}
                      {status !== "loaded" && (
                        <Skeleton
                          variant="rounded"
                          animation={status === "error" ? false : "wave"}
                          sx={{
                            position: "absolute",
                            inset: 0,
                            transform: "none",
                            width: "100%",
                            height: "100%",
                          }}
                        />
                      )}
                      {status === "error" && (
                        <Typography
                          variant="caption"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 1,
                            textAlign: "center",
                            color: "text.secondary",
                            bgcolor: "grey.100",
                          }}
                        >
                          הקובץ לא נטען
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      title={captionFor(image)}
                      sx={{
                        display: "block",
                        mt: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {captionFor(image)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Stack>

      <Stack
        gap={1.5}
        sx={{
          width: 320,
          flexShrink: 0,
          maxHeight: PANEL_MAX_HEIGHT,
          overflow: "auto",
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        {!selectedImage ? (
          <Typography variant="body2" color="text.secondary">
            בחרו תמונה כדי לערוך את השיוך שלה.
          </Typography>
        ) : (
          <>
            {previewFailed ? (
              <Typography variant="body2" color="text.secondary">
                הקובץ לא נטען. אפשר להסיר את השיוך או למחוק את הרשומה.
              </Typography>
            ) : (
              <Box
                component="img"
                src={sameOriginImageURL(selectedImage.imageId)}
                alt="תמונה נבחרת"
                onError={() => setPreviewFailed(true)}
                sx={{
                  width: "100%",
                  height: 160,
                  objectFit: "cover",
                  borderRadius: 1,
                }}
              />
            )}
            {categories.map((category) => {
              const selectedCount = category.subs.filter((sub) =>
                draftSubs.includes(sub.id),
              ).length;
              return (
                <Accordion
                  key={category.id}
                  disableGutters
                  elevation={0}
                  expanded={openCategoryIds.includes(category.id)}
                  onChange={(_, expanded) =>
                    setOpenCategoryIds((current) =>
                      expanded
                        ? [...current, category.id]
                        : current.filter((id) => id !== category.id),
                    )
                  }
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2">
                      {selectedCount > 0
                        ? `${category.name} (${selectedCount})`
                        : category.name || "קטגוריה"}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {category.subs.map((sub) => (
                      <ListItemButton
                        key={sub.id}
                        component="div"
                        onClick={() =>
                          toggleId(draftSubs, sub.id, setDraftSubs)
                        }
                        sx={{ py: 0.25, borderRadius: 1 }}
                      >
                        <Checkbox
                          size="small"
                          edge="start"
                          tabIndex={-1}
                          disableRipple
                          checked={draftSubs.includes(sub.id)}
                          sx={{ p: 0.25, pointerEvents: "none" }}
                        />
                        <ListItemText
                          primary={sub.name || "תת-קטגוריה"}
                          primaryTypographyProps={{
                            noWrap: true,
                            fontSize: "0.875rem",
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })}
            <Accordion
              disableGutters
              elevation={0}
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">
                  {`אוכלוסייה, רק לסינון ידני${
                    draftPopulations.length
                      ? ` (${draftPopulations.length})`
                      : ""
                  }`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {populations.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    אין אוכלוסיות.
                  </Typography>
                ) : (
                  populations.map((population) => (
                    <ListItemButton
                      key={population.id}
                      component="div"
                      onClick={() =>
                        toggleId(
                          draftPopulations,
                          population.id,
                          setDraftPopulations,
                        )
                      }
                      sx={{ py: 0.25, borderRadius: 1 }}
                    >
                      <Checkbox
                        size="small"
                        edge="start"
                        tabIndex={-1}
                        disableRipple
                        checked={draftPopulations.includes(population.id)}
                        sx={{ p: 0.25, pointerEvents: "none" }}
                      />
                      <ListItemText
                        primary={text(population.name) || "אוכלוסייה"}
                        primaryTypographyProps={{
                          noWrap: true,
                          fontSize: "0.875rem",
                        }}
                      />
                    </ListItemButton>
                  ))
                )}
              </AccordionDetails>
            </Accordion>
            <Stack direction="row" gap={1}>
              <Button
                variant="contained"
                disabled={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                {saveMutation.isPending ? (
                  <CircularProgress size={22} />
                ) : (
                  "שמירה"
                )}
              </Button>
              <Button
                color="error"
                variant="outlined"
                onClick={() => onDelete(selectedImage.imageId)}
              >
                מחיקה
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Stack>
  );
};
